import { mutationOptions, queryOptions } from '@tanstack/react-query';

import { PortalApiClient } from '../../../rest-api/portalApiClient';
import { tagKeys, type TagTile } from '../../project/components/ProjectsCore.queries';

export type CourseExportRow = Awaited<ReturnType<PortalApiClient['listCourseExports']>>[number];

export type CourseExportSortOption = 'Newest' | 'Oldest' | 'Alphabetical' | 'ReverseAlphabetical';

export const courseExportKeys = {
  all: ['courseExports'] as const,
  lists: () => [...courseExportKeys.all, 'list'] as const,
} as const;

export const getListCourseExportsQueryOptions = (portalApiClient: Pick<PortalApiClient, 'listCourseExports'>) =>
  queryOptions({
    queryKey: courseExportKeys.lists(),
    queryFn: async () => await portalApiClient.listCourseExports(),
  });

export const getSetTagToCourseExportMutationOptions = (
  portalApiClient: Pick<PortalApiClient, 'setTagToCourseExport'>
) =>
  mutationOptions({
    mutationFn: portalApiClient.setTagToCourseExport,
    // The chip has to appear the instant it is clicked, so the listing cache is written before the
    // request goes out and rolled back if it fails. Writing the cache rather than deriving the chips
    // from the in-flight variables (as the courses page does) keeps the chip stable after the
    // mutation settles, while the refetch triggered in onSettled is still in flight.
    onMutate: (variables, context) => {
      const previousRows = context.client.getQueryData<CourseExportRow[]>(courseExportKeys.lists());
      if (!previousRows) {
        return { previousRows };
      }

      const tag = context.client
        .getQueryData<TagTile[]>(tagKeys.lists())
        ?.find((candidate) => candidate.guid === variables.tagGuid);

      if (variables.assign && !tag) {
        // Nothing to render a chip from; leave the cache alone and let the refetch fill it in.
        return { previousRows };
      }

      const updatedRows = previousRows.map((row) => {
        if (row.guid !== variables.courseExportGuid) {
          return row;
        }

        const withoutTag = row.tags.filter((rowTag) => rowTag.guid !== variables.tagGuid);
        if (!variables.assign || !tag) {
          return { ...row, tags: withoutTag };
        }

        // The listing returns tags alphabetically, so the optimistic row has to match or the chip
        // would jump when the refetch lands.
        const tags = [...withoutTag, { guid: tag.guid, name: tag.name, color: tag.color }].sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
        );
        return { ...row, tags };
      });

      context.client.setQueryData(courseExportKeys.lists(), updatedRows);
      return { previousRows };
    },
    onError: (_error, _variables, onMutateResult, context) => {
      if (onMutateResult?.previousRows) {
        context.client.setQueryData(courseExportKeys.lists(), onMutateResult.previousRows);
      }
    },
    onSettled: (_data, _error, _variables, _onMutateResult, context) => {
      void context.client.invalidateQueries({ queryKey: courseExportKeys.lists() });
    },
  });

export const filterCourseExports = (
  rows: CourseExportRow[],
  filters: { search: string; tagNames: string[] }
): CourseExportRow[] => {
  const search = filters.search.trim().toLowerCase();
  return rows.filter((row) => {
    const matchesSearch = search.length === 0 || row.name.toLowerCase().includes(search);
    const matchesTags =
      filters.tagNames.length === 0 ||
      filters.tagNames.every((tagName) => row.tags.some((tag) => tag.name === tagName));
    return matchesSearch && matchesTags;
  });
};

export const sortCourseExports = (rows: CourseExportRow[], sort: CourseExportSortOption): CourseExportRow[] => {
  const sorted = [...rows];
  switch (sort) {
    case 'Oldest':
      return sorted.sort((a, b) => a.createdAtTimestamp - b.createdAtTimestamp);
    case 'Alphabetical':
      return sorted.sort((a, b) => a.name.localeCompare(b.name));
    case 'ReverseAlphabetical':
      return sorted.sort((a, b) => b.name.localeCompare(a.name));
    case 'Newest':
    default:
      return sorted.sort((a, b) => b.createdAtTimestamp - a.createdAtTimestamp);
  }
};

export {
  getListTagsQueryOptions,
  getUpdateTagMutationOptions,
  getDeleteTagMutationOptions,
  type TagTile,
} from '../../project/components/ProjectsCore.queries';

export type CreateCourseExportTagParams = Parameters<PortalApiClient['createTag']>[0] & {
  /**
   * Assigns the new tag to this module as part of creating it. A tag created from a row's dropdown
   * is meant for that row, so it is assigned straight away — the same as creating one from a course
   * tile on the Courses page.
   */
  courseExportGuidToAssign?: string;
};

/**
 * Creates a tag and, when asked, assigns it to a module in the same step. The courses page has its
 * own version of this that assigns to a project instead; the two differ only in what they assign to.
 */
export const getCreateTagMutationOptions = (
  portalApiClient: Pick<PortalApiClient, 'createTag' | 'setTagToCourseExport'>
) =>
  mutationOptions({
    mutationFn: async (variables: CreateCourseExportTagParams) => {
      const tag = await portalApiClient.createTag(variables);
      if (!tag.guid) {
        throw new Error('Created tag is missing guid');
      }

      if (variables.courseExportGuidToAssign) {
        await portalApiClient.setTagToCourseExport({
          courseExportGuid: variables.courseExportGuidToAssign,
          tagGuid: tag.guid,
          assign: true,
        });
      }

      return { ...tag, guid: tag.guid };
    },
    onSettled: (_data, _error, variables, _onMutateResult, context) => {
      // The new tag has to appear in the filter and row dropdowns straight away.
      void context.client.invalidateQueries({ queryKey: tagKeys.lists() });
      if (variables.courseExportGuidToAssign) {
        void context.client.invalidateQueries({ queryKey: courseExportKeys.lists() });
      }
    },
  });
